import { useCallback, useEffect, useRef, useState } from 'react';

import { AlignLeftOutlined, EyeOutlined } from '@ant-design/icons';
import { MenuProps } from 'antd';
import { TableProps } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { Role } from 'models/Role';

import { RoleService } from 'services/RoleService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import { PAGE_SIZE, RoleModalHandlers, TableParams } from '../types';

interface UseRoleTableParams {
  message: MessageInstance;
  setTableLoading: (loading: boolean) => void;
  modalHandlers: RoleModalHandlers;
}

export interface UseRoleTableReturn {
  filteredDataSource: Role[];
  tableParams: TableParams;
  fetchDataFromServer: (withLoading?: boolean) => Promise<void>;
  onSearch: (value: string) => void;
  handleTableChange: TableProps<Role>['onChange'];
  getDropdownMenu: (row: Role) => MenuProps['items'];
}

export function useRoleTable({
  message,
  setTableLoading,
  modalHandlers,
}: UseRoleTableParams): UseRoleTableReturn {
  const { t } = useTranslation();

  const dataSourceRef = useRef<Role[]>([]);
  const [filteredDataSource, setFilteredDataSource] = useState<Role[]>([]);
  const [term, setTerm] = useState('');
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
  });

  useEffect(() => {
    fetchDataFromServer(false);
  }, []);

  useEffect(() => {
    applyFilter(1);
  }, [term]);

  useEffect(() => {
    applyFilter(tableParams.pagination?.current);
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams.sortField,
    tableParams.sortOrder,
  ]);

  const fetchDataFromServer = async (withLoading = false) => {
    if (withLoading) {
      setTableLoading(true);
    }

    const response = await RoleService.getByCurrentCompany();

    if (response.success) {
      dataSourceRef.current = response.data as Role[];
      applyFilter(1, dataSourceRef.current);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.settings.roles.scene.errors.fetch_roles'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const applyFilter = useCallback(
    (currentPage = 1, data?: Role[]) => {
      const termFilter = FunctionsHelper.normalizeText(term.trim());
      let filteredData = data !== undefined ? data : [...dataSourceRef.current];

      filteredData = filteredData.sort((a, b) => b.id - a.id);

      if (termFilter.length > 0) {
        filteredData = filteredData.filter((record) =>
          FunctionsHelper.normalizeText(record.name).includes(termFilter)
        );
      }

      const pageSize = PAGE_SIZE;
      const total = filteredData.length;
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      setFilteredDataSource(paginatedData);
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          current: currentPage,
          total,
        },
      }));
    },
    [term]
  );

  const onSearch = useCallback((value: string) => {
    setTerm(value);
  }, []);

  const handleTableChange: TableProps<Role>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<Role> | SorterResult<Role>[]
    ) => {
      const sorterLocal = sorter as SorterResult<Role>;

      setTableParams({
        pagination,
        filters,
        sortOrder:
          sorterLocal.order && sorterLocal.columnKey
            ? sorterLocal.order
            : undefined,
        sortField:
          sorterLocal.order && sorterLocal.columnKey
            ? String(sorterLocal.columnKey)
            : undefined,
      });

      if (pagination.pageSize !== PAGE_SIZE) {
        setFilteredDataSource([]);
      }
    },
    []
  );

  const getDropdownMenu = useCallback(
    (row: Role): MenuProps['items'] => {
      const menus: MenuProps['items'] = [];
      let itemNro = 1;

      menus.push({
        key: `${itemNro++}`,
        label: (
          <span>
            <EyeOutlined style={{ marginRight: '8px' }} />{' '}
            {t('scenes.settings.roles.scene.dropdown.view_detail')}
          </span>
        ),
        onClick: () => modalHandlers.openDetailModal(row),
      });

      menus.push({
        key: `${itemNro++}`,
        label: (
          <span>
            <AlignLeftOutlined style={{ marginRight: '8px' }} />{' '}
            {t('scenes.settings.roles.scene.dropdown.menus')}
          </span>
        ),
        onClick: () => modalHandlers.openMenuDetailModal(row),
      });

      return menus;
    },
    [t, modalHandlers]
  );

  return {
    filteredDataSource,
    tableParams,
    fetchDataFromServer,
    onSearch,
    handleTableChange,
    getDropdownMenu,
  };
}
